/**
 * LeftSidebar Component
 * Clean left sidebar with Layers, Filters, and Tools sections
 * Based on reference UI design
 */

import { useState, useCallback } from 'react';
import type { FloorPlanOverlay } from '../../../shared/types/floorplan';
import './LeftSidebar.css';

export type MediaType =
  | 'hdImages'
  | '360Images'
  | '3dModels'
  | 'pointClouds'
  | 'floorTours'
  | 'projectPhotos'
  | 'videos'
  | 'sketches';

export interface MediaTypeConfig {
  id: MediaType;
  label: string;
  icon: string;
  count: number;
  visible: boolean;
}

export interface OverlayItem {
  id: string;
  name: string;
  visible: boolean;
}

interface LeftSidebarProps {
  // Layers
  basemap: 'satellite' | 'hybrid' | 'streets' | 'none';
  onBasemapChange: (basemap: 'satellite' | 'hybrid' | 'streets' | 'none') => void;
  overlays: OverlayItem[];
  onOverlayVisibilityChange: (id: string, visible: boolean) => void;

  // Media types
  mediaTypes: MediaTypeConfig[];
  onMediaTypeToggle: (id: MediaType) => void;

  // Floor plans
  floorPlans?: FloorPlanOverlay[];
  onAddFloorPlan?: () => void;
  onFloorPlanVisibilityChange?: (id: string, visible: boolean) => void;
  onFloorPlanOpacityChange?: (id: string, opacity: number) => void;
  onFloorPlanEdit?: (id: string) => void;
  onFloorPlanDelete?: (id: string) => void;

  // Tools
  onMeasureDistance: () => void;
  onMeasureArea: () => void;
  onAddPointOfInterest?: () => void;
  onAddAreaOfInterest?: () => void;

  // State
  activeTool?: 'measure-distance' | 'measure-area' | 'point-of-interest' | 'area-of-interest';
}

type ExpandedSection = 'layers' | 'filters' | 'tools' | null;

export function LeftSidebar({
  basemap,
  onBasemapChange,
  overlays,
  onOverlayVisibilityChange,
  mediaTypes,
  onMediaTypeToggle,
  floorPlans = [],
  onAddFloorPlan,
  onFloorPlanVisibilityChange,
  onFloorPlanOpacityChange,
  onFloorPlanEdit,
  onFloorPlanDelete,
  onMeasureDistance,
  onMeasureArea,
  onAddPointOfInterest,
  onAddAreaOfInterest,
  activeTool,
}: LeftSidebarProps) {
  // ─── Smooth floor plan opacity slider: local state for responsiveness ──
  const [localFpOpacities, setLocalFpOpacities] = useState<Record<string, number>>({});

  const handleFpOpacityInput = useCallback((id: string, value: number) => {
    setLocalFpOpacities(prev => ({ ...prev, [id]: value }));
    onFloorPlanOpacityChange?.(id, value);
  }, [onFloorPlanOpacityChange]);

  const handleFpOpacityCommit = useCallback((id: string) => {
    setLocalFpOpacities(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const [expandedSection, setExpandedSection] = useState<ExpandedSection>('layers');
  const [layersExpanded, setLayersExpanded] = useState({
    base: false,
    overlays: false,
    mediaType: false,
    floorPlans: true, // Start expanded so users can find it easily
  });

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const toggleLayerSubsection = (subsection: 'base' | 'overlays' | 'mediaType' | 'floorPlans') => {
    setLayersExpanded(prev => ({
      ...prev,
      [subsection]: !prev[subsection],
    }));
  };

  // Get media type icon SVG
  const getMediaIcon = (iconType: string) => {
    switch (iconType) {
      case 'hd-image':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        );
      case '360':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
            <path d="M2 12h20" />
          </svg>
        );
      case '3d':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l9 5v10l-9 5-9-5V7l9-5z" />
            <path d="M12 22V12" />
            <path d="M21 7l-9 5-9-5" />
          </svg>
        );
      case 'pointcloud':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="6" cy="6" r="2" />
            <circle cx="18" cy="6" r="2" />
            <circle cx="6" cy="18" r="2" />
            <circle cx="18" cy="18" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="6" r="1" />
            <circle cx="12" cy="18" r="1" />
            <circle cx="6" cy="12" r="1" />
            <circle cx="18" cy="12" r="1" />
          </svg>
        );
      case 'floor':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
            <path d="M9 21V9" />
          </svg>
        );
      case 'photo':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        );
      case 'video':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="6" width="15" height="12" rx="2" />
            <path d="M17 10l5-3v10l-5-3v-4z" />
          </svg>
        );
      case 'sketch':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 19l7-7 3 3-7 7-3-3z" />
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
            <path d="M2 2l7.586 7.586" />
            <circle cx="11" cy="11" r="2" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
          </svg>
        );
    }
  };

  return (
    <div className="left-sidebar">
      {/* Layers Section */}
      <div className="sidebar-section">
        <button
          className={`sidebar-section-btn ${expandedSection === 'layers' ? 'active' : ''}`}
          onClick={() => toggleSection('layers')}
        >
          <svg className="sidebar-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          <span>Layers</span>
        </button>

        {expandedSection === 'layers' && (
          <div className="sidebar-section-content">
            {/* Base Layer */}
            <div className="sidebar-subsection">
              <button
                className="sidebar-subsection-btn"
                onClick={() => toggleLayerSubsection('base')}
              >
                <span>Base</span>
                <svg className={`sidebar-chevron ${layersExpanded.base ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {layersExpanded.base && (
                <div className="sidebar-subsection-content">
                  <label className="sidebar-radio">
                    <input
                      type="radio"
                      name="basemap"
                      checked={basemap === 'satellite'}
                      onChange={() => onBasemapChange('satellite')}
                    />
                    <span>Satellite</span>
                  </label>
                  <label className="sidebar-radio">
                    <input
                      type="radio"
                      name="basemap"
                      checked={basemap === 'hybrid'}
                      onChange={() => onBasemapChange('hybrid')}
                    />
                    <span>Hybrid</span>
                  </label>
                  <label className="sidebar-radio">
                    <input
                      type="radio"
                      name="basemap"
                      checked={basemap === 'streets'}
                      onChange={() => onBasemapChange('streets')}
                    />
                    <span>Streets</span>
                  </label>
                  <label className="sidebar-radio">
                    <input
                      type="radio"
                      name="basemap"
                      checked={basemap === 'none'}
                      onChange={() => onBasemapChange('none')}
                    />
                    <span>None</span>
                  </label>
                </div>
              )}
            </div>

            {/* Overlays */}
            {overlays.length > 0 && (
              <div className="sidebar-subsection">
                <button
                  className="sidebar-subsection-btn"
                  onClick={() => toggleLayerSubsection('overlays')}
                >
                  <span>Overlays</span>
                  <svg className={`sidebar-chevron ${layersExpanded.overlays ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {layersExpanded.overlays && (
                  <div className="sidebar-subsection-content">
                    {overlays.map(overlay => (
                      <label key={overlay.id} className="sidebar-checkbox">
                        <input
                          type="checkbox"
                          checked={overlay.visible}
                          onChange={() => onOverlayVisibilityChange(overlay.id, !overlay.visible)}
                        />
                        <span>{overlay.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Media Type */}
            <div className="sidebar-subsection">
              <button
                className="sidebar-subsection-btn"
                onClick={() => toggleLayerSubsection('mediaType')}
              >
                <span>Media Type</span>
                <svg className={`sidebar-chevron ${layersExpanded.mediaType ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {layersExpanded.mediaType && (
                <div className="sidebar-subsection-content sidebar-media-types">
                  {mediaTypes.map(media => (
                    <button
                      key={media.id}
                      className={`sidebar-media-btn ${media.visible ? 'active' : ''}`}
                      onClick={() => onMediaTypeToggle(media.id)}
                      title={`${media.label} (${media.count})`}
                    >
                      {getMediaIcon(media.icon)}
                      <span className="sidebar-media-label">{media.label}</span>
                      {media.count > 0 && (
                        <span className="sidebar-media-count">{media.count}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Floor Plans */}
            {onAddFloorPlan && (
              <div className="sidebar-subsection">
                <button
                  className="sidebar-subsection-btn"
                  onClick={() => toggleLayerSubsection('floorPlans')}
                >
                  <span>Floor Plans</span>
                  <svg className={`sidebar-chevron ${layersExpanded.floorPlans ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {layersExpanded.floorPlans && (
                  <div className="sidebar-subsection-content">
                    <button
                      className="sidebar-add-btn"
                      onClick={onAddFloorPlan}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      <span>Add Floor Plan</span>
                    </button>

                    {floorPlans
                      .sort((a, b) => a.floorNumber - b.floorNumber)
                      .map(fp => (
                        <div key={fp.id} className="floor-plan-item">
                          <label className="sidebar-checkbox floor-plan-checkbox">
                            <input
                              type="checkbox"
                              checked={fp.visible}
                              onChange={() => onFloorPlanVisibilityChange?.(fp.id, !fp.visible)}
                            />
                            <span className="floor-plan-name">{fp.name}</span>
                          </label>
                          <div className="floor-plan-controls">
                            <input
                              type="range"
                              className="floor-plan-opacity"
                              min="0"
                              max="100"
                              step="1"
                              value={localFpOpacities[fp.id] !== undefined ? localFpOpacities[fp.id] * 100 : fp.opacity * 100}
                              onInput={(e) => handleFpOpacityInput(fp.id, parseInt((e.target as HTMLInputElement).value) / 100)}
                              onChange={() => {/* handled by onInput */}}
                              onPointerUp={() => handleFpOpacityCommit(fp.id)}
                              onMouseUp={() => handleFpOpacityCommit(fp.id)}
                              title={`Opacity: ${Math.round((localFpOpacities[fp.id] ?? fp.opacity) * 100)}%`}
                            />
                            <button
                              className="floor-plan-action-btn"
                              onClick={() => onFloorPlanEdit?.(fp.id)}
                              title="Edit alignment"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              className="floor-plan-action-btn floor-plan-delete-btn"
                              onClick={() => onFloorPlanDelete?.(fp.id)}
                              title="Delete floor plan"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters Section */}
      <div className="sidebar-section">
        <button
          className={`sidebar-section-btn ${expandedSection === 'filters' ? 'active' : ''}`}
          onClick={() => toggleSection('filters')}
        >
          <svg className="sidebar-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <span>Filters</span>
        </button>

        {expandedSection === 'filters' && (
          <div className="sidebar-section-content">
            <p className="sidebar-empty-text">No filters applied</p>
          </div>
        )}
      </div>

      {/* Tools Section */}
      <div className="sidebar-section">
        <button
          className={`sidebar-section-btn ${expandedSection === 'tools' ? 'active' : ''}`}
          onClick={() => toggleSection('tools')}
        >
          <svg className="sidebar-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
          </svg>
          <span>Tools</span>
        </button>

        {expandedSection === 'tools' && (
          <div className="sidebar-section-content sidebar-tools">
            <button
              className={`sidebar-tool-btn ${activeTool === 'measure-distance' ? 'active' : ''}`}
              onClick={onMeasureDistance}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 12h20M2 12l4-4M2 12l4 4M22 12l-4-4M22 12l-4 4" />
              </svg>
              <span>Measure Distance</span>
            </button>
            <button
              className={`sidebar-tool-btn ${activeTool === 'measure-area' ? 'active' : ''}`}
              onClick={onMeasureArea}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3h18v18H3z" />
              </svg>
              <span>Measure Area</span>
            </button>
            {onAddPointOfInterest && (
              <button
                className={`sidebar-tool-btn ${activeTool === 'point-of-interest' ? 'active' : ''}`}
                onClick={onAddPointOfInterest}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span>Point of Interest</span>
              </button>
            )}
            {onAddAreaOfInterest && (
              <button
                className={`sidebar-tool-btn ${activeTool === 'area-of-interest' ? 'active' : ''}`}
                onClick={onAddAreaOfInterest}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                </svg>
                <span>Area of Interest</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
